import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/da4eo1r0muk3qhp2tjs0';

const LABEL_IMAGE = 'https://storage.helloreaddy.io/project_files/179a8203-21a2-4b5f-9e7d-c89fcdd8c7e7/96e7480e-f581-4469-a31c-90b07df99dcf_compressed_562884e8-96dd-4534-97bf-c8961128b502.webp';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleDownloadLabel = async () => {
    try {
      const res = await fetch(LABEL_IMAGE);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'carte-adresse-godwin-chine.jpeg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(LABEL_IMAGE, '_blank');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honey = String(fd.get('website_alt') || '').trim();
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }
    fd.delete('website_alt');

    const message = String(fd.get('message') || '');
    if (message.length > 500) {
      setStatus('error');
      setErrorMsg('Le message ne doit pas dépasser 500 caractères.');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const params = new URLSearchParams();
      fd.forEach((v, k) => params.append(k, String(v)));
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const code = parsed?.code;
      const msg = parsed?.meta?.message || parsed?.meta?.detail || '';

      if (res.ok && code === 'OK' && !String(msg).toLowerCase().includes('spam')) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(msg || "L'envoi a échoué, veuillez réessayer.");
      }
    } catch {
      setStatus('error');
      setErrorMsg('Erreur réseau. Veuillez réessayer.');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-10">

        <div className="mb-14 rounded-lg overflow-hidden border border-background-200 bg-background-100">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 md:p-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider">
                <i className="ri-download-2-line w-4 h-4 flex items-center justify-center"></i>
                Pour votre fournisseur
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4 leading-tight">
                Carte adresse — Entrepôt Chine
              </h2>
              <p className="mt-4 text-foreground-700 leading-relaxed">
                Téléchargez cette carte adresse et envoyez-la à votre fournisseur chinois. Il pourra ainsi expédier directement vos marchandises vers l'entrepôt Godwin Logistics en Chine.
              </p>

              <div className="mt-6 p-4 rounded-md bg-secondary-50 border border-secondary-200">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 shrink-0">
                    <i className="ri-map-pin-2-line text-sm"></i>
                  </div>
                  <div className="text-sm text-foreground-700 leading-relaxed">
                    <strong className="text-foreground-900">Adresse d'expédition (Chine)</strong><br/>
                    广州市白云区人和镇建南村庙仔街南广州得劳斯佳人集装箱厂内<br/>
                    <span className="font-semibold text-primary-700">入仓号 : CTB670-B</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownloadLabel}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white font-semibold whitespace-nowrap cursor-pointer transition"
              >
                <i className="ri-download-2-line w-5 h-5 flex items-center justify-center"></i>
                Télécharger la carte adresse
              </button>
              <p className="mt-3 text-xs text-foreground-500">Format image · À transmettre à votre fournisseur chinois</p>
            </div>

            <div className="relative bg-background-200/40 min-h-[320px] flex items-center justify-center p-6 md:p-10">
              <img
                src={LABEL_IMAGE}
                alt="Carte adresse entrepôt Chine Godwin Logistics"
                className="max-h-[420px] w-auto max-w-full object-contain rounded-md border border-background-300"
              />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <span className="text-primary-600 text-sm font-semibold uppercase tracking-widest">Contact</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mt-3 leading-tight">
              Prêt à expédier ?<br/>Parlons de votre prochain conteneur.
            </h2>
            <p className="mt-5 text-foreground-700 leading-relaxed">
              Décrivez-nous votre besoin (nature de la marchandise, volume, ports de départ/arrivée) et notre équipe revient vers vous sous 24h avec un devis clair.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4 p-4 rounded-md bg-background-100 border border-background-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-white shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500 font-semibold">Email</div>
                  <div className="text-foreground-900 font-medium mt-0.5 text-sm">
                    info@godwin-logistics.com<br/>
                    sympho.kobabiya@godwin-logistics.com<br/>
                    blaise.mununga@godwin-logistics.com
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-md bg-background-100 border border-background-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-white shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500 font-semibold">Téléphone</div>
                  <div className="text-foreground-900 font-medium mt-0.5 text-sm">
                    Sympho : +243 993 619 615 / +243 891 281 032<br/>
                    Blaise : +243 846 959 425<br/>
                    Alex : +243 999 209 934<br/>
                    Chine (Xiao Li) : +86 150 740 580 58 / +86 130 251 443 69
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-md bg-background-100 border border-background-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-white shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500 font-semibold">Adresses</div>
                  <div className="text-foreground-900 font-medium mt-0.5 text-sm">
                    Siège : Bâtiment AMAZON, 461 Av. des usines / Kapenda, 1er niveau, Lubumbashi RDC<br/>
                    Entrepôt Chine : 广州市白云区人和镇建南村庙仔街南广州得劳斯佳人集装箱厂内 (入仓号 CTB670-B)
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-md bg-background-100 border border-background-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-white shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500 font-semibold">Disponibilité</div>
                  <div className="text-foreground-900 font-medium mt-0.5">Lun – Sam · 8h – 19h</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form
              id="contact-godwin"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg border border-background-200 p-6 md:p-8"
            >
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Demandez votre devis</h3>
              <p className="text-sm text-foreground-600 mt-1">Remplissez le formulaire — réponse sous 24h.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-foreground-700">Prénom</label>
                  <input name="first_name" required className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground-700">Nom</label>
                  <input name="last_name" required className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none" />
                </div>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-foreground-700">Email</label>
                  <input type="email" name="email" required className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground-700">Téléphone</label>
                  <input name="phone" className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none" />
                </div>
              </div>

              <div className="mt-4">
                <label className="text-xs font-semibold text-foreground-700">Type de solution</label>
                <select name="solution" className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none cursor-pointer">
                  <option>FCL - Conteneur complet</option>
                  <option>LCL - Groupage maritime</option>
                  <option>House to House</option>
                  <option>Projet industriel / hors gabarit</option>
                  <option>Gestion douanière</option>
                  <option>Transaction de fonds</option>
                  <option>Autre besoin</option>
                </select>
              </div>

              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-foreground-700">Port de départ</label>
                  <input name="origin" placeholder="ex : Guangzhou / Shenzhen" className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-foreground-700">Port d'arrivée</label>
                  <input name="destination" placeholder="ex : Lubumbashi / Matadi" className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none" />
                </div>
              </div>

              <div className="mt-4">
                <label className="text-xs font-semibold text-foreground-700">Décrivez votre besoin</label>
                <textarea name="message" maxLength={500} rows={4} className="mt-1 w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 focus:border-primary-500 focus:outline-none resize-none" />
                <div className="text-xs text-foreground-500 mt-1">Max. 500 caractères</div>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-website_alt"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-6 w-full px-6 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold whitespace-nowrap cursor-pointer transition inline-flex items-center justify-center gap-2"
              >
                {status === 'loading' ? 'Envoi en cours...' : (<>Envoyer la demande <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i></>)}
              </button>

              {status === 'success' && (
                <div className="mt-4 p-3 rounded-md bg-primary-100 border border-primary-200 text-primary-800 text-sm">
                  Merci ! Votre demande a bien été envoyée, nous revenons vers vous rapidement.
                </div>
              )}
              {status === 'error' && (
                <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                  {errorMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}